import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

export default function LegalLayout({ doc, titleSuffix, testId = "legal-page" }) {
  useEffect(() => {
    document.title = `${doc.title} — Nom Apps LLC`;
    if (doc.description) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", doc.description);
    }
  }, [doc.title, doc.description]);

  return (
    <>
      <Nav />
      <main className="pt-32 sm:pt-36" data-testid={testId}>
        <div className="mx-auto max-w-3xl px-6 pb-24">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-nom-border bg-white px-4 py-2 text-sm font-extrabold text-nom-inkSoft transition-colors hover:border-nom-coral hover:text-nom-coral"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to home
          </Link>

          <Reveal>
            <p className="mt-10 text-xs font-extrabold uppercase tracking-[0.18em] text-nom-coral">Legal</p>
            <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-nom-ink sm:text-5xl">
              {doc.title}
            </h1>
            <p className="mt-4 text-sm font-bold text-nom-muted" data-testid="legal-meta">
              {doc.scope}
              {doc.version ? ` · Version ${doc.version} · Effective ${doc.effective}` : ""}
            </p>
            {doc.intro && (
              <p className="mt-6 rounded-3xl border border-nom-border bg-nom-peach/50 p-5 text-sm leading-relaxed text-nom-ink">
                {doc.intro}
              </p>
            )}
          </Reveal>

          <div className="mt-10 space-y-8" data-testid="legal-sections">
            {doc.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-display text-xl font-semibold text-nom-ink">{s.h}</h2>
                {s.p && s.p.map((para, i) => (
                  <p key={i} className="mt-3 text-sm leading-relaxed text-nom-inkSoft">
                    {para}
                  </p>
                ))}
                {s.steps && (
                  <ol className="mt-4 space-y-3">
                    {s.steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-nom-inkSoft">
                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-nom-peach font-display text-xs font-semibold text-nom-ink">
                          {i + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            ))}
          </div>

          <p className="mt-12 rounded-3xl bg-nom-peach/50 p-5 text-xs font-bold leading-relaxed text-nom-inkSoft">
            Questions? Email us at support@nomappsllc.com. NomNom is a product of Nom Apps LLC{titleSuffix ? ` · ${titleSuffix}` : ""}.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}
